import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Heart, MessageCircle, Send, Image, X, 
  Trash2, Hash, Info, Users, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { API } from '@/App';

function CommunityPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [newPostImages, setNewPostImages] = useState([]);
  const [newPostHashtags, setNewPostHashtags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [guidelines, setGuidelines] = useState('');
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const currentUserId = localStorage.getItem('user_id');
  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const fetchPosts = useCallback(async (pageNum = 1, append = false) => {
    try {
      const response = await axios.get(`${API}/community/posts?page=${pageNum}&limit=10`, config);
      if (append) {
        setPosts(prev => [...prev, ...response.data.posts]);
      } else {
        setPosts(response.data.posts);
      }
      setHasMore(pageNum < response.data.pages);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load posts');
      setLoading(false);
    }
  }, []);

  const fetchGuidelines = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/guidelines`, config);
      setGuidelines(response.data.guidelines);
    } catch (error) {
      console.error('Failed to load guidelines');
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchGuidelines();
  }, [fetchPosts, fetchGuidelines]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (newPostImages.length + files.length > 5) {
      toast.error('Maximum 5 images allowed per post');
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/community/upload`, formData, {
          headers: {
            ...config.headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('Upload response:', response.data);
        setNewPostImages(prev => [...prev, response.data.url]);
      }
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    }
    setUploading(false);
  };

  const removeImage = (index) => {
    setNewPostImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitPost = async () => {
    if (!newPost.trim() && newPostImages.length === 0) {
      toast.error('Please write something or add an image to share');
      return;
    }

    try {
      const hashtags = newPostHashtags
        .split(/[\s,]+/)
        .filter(tag => tag.trim())
        .map(tag => tag.replace('#', ''));

      await axios.post(`${API}/community/posts`, {
        content: newPost,
        images: newPostImages,
        hashtags
      }, config);

      toast.success('Post shared with the community!');
      setNewPost('');
      setNewPostImages([]);
      setNewPostHashtags('');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(`${API}/community/posts/${postId}/like`, {}, config);
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const newLikes = response.data.action === 'liked'
            ? [...post.likes, currentUserId]
            : post.likes.filter(id => id !== currentUserId);
          return { ...post, likes: newLikes };
        }
        return post;
      }));
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleComment = async (postId) => {
    const comment = commentText[postId];
    if (!comment?.trim()) return;

    try {
      const response = await axios.post(`${API}/community/posts/${postId}/comments`, {
        content: comment
      }, config);

      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return { ...post, comments: [...post.comments, response.data.comment] };
        }
        return post;
      }));

      setCommentText(prev => ({ ...prev, [postId]: '' }));
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/community/posts/${postId}`, config);
      setPosts(prev => prev.filter(post => post.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await axios.delete(`${API}/community/posts/${postId}/comments/${commentId}`, config);
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return { ...post, comments: post.comments.filter(c => c.id !== commentId) };
        }
        return post;
      }));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage, true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Users className="w-16 h-16 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-secondary-700 text-lg">Loading community...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="hover:bg-primary/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-serif font-bold text-secondary-900 flex items-center gap-2">
                  <Users className="w-6 h-6 text-primary" />
                  Radiant Community
                </h1>
                <p className="text-sm text-secondary-600">Share, encourage, and grow together</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGuidelines(!showGuidelines)}
              className="flex items-center gap-2"
            >
              <Info className="w-4 h-4" />
              Guidelines
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Guidelines Modal */}
        {showGuidelines && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-serif font-bold text-secondary-900">Community Guidelines</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowGuidelines(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="prose prose-sm max-w-none text-secondary-700 whitespace-pre-line">
              {guidelines}
            </div>
          </div>
        )}

        {/* Create Post */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6" data-testid="create-post-form">
          <h3 className="font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Share with the Community
          </h3>
          
          <Textarea
            value={newPost}
            onChange={(e) => setNewPost(e.target.value)}
            placeholder="What's on your mind? Share your achievements, challenges, or encouragement..."
            className="min-h-[120px] mb-4 resize-none"
            data-testid="post-content-input"
          />

          {/* Image Previews */}
          {newPostImages.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {newPostImages.map((url, index) => (
                <div key={index} className="relative">
                  <img
                    src={url}
                    alt={`Upload ${index + 1}`}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Hashtags */}
          <Input
            value={newPostHashtags}
            onChange={(e) => setNewPostHashtags(e.target.value)}
            placeholder="Add hashtags (e.g., achievement, recipe, workout)"
            className="mb-4"
            data-testid="post-hashtags-input"
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploading || newPostImages.length >= 5}
                />
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  newPostImages.length >= 5 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-stone-50 cursor-pointer'
                }`}>
                  <Image className="w-5 h-5 text-secondary-600" />
                  <span className="text-sm text-secondary-600">
                    {uploading ? 'Uploading...' : `Add Photos (${newPostImages.length}/5)`}
                  </span>
                </div>
              </label>
            </div>
            <Button
              onClick={handleSubmitPost}
              disabled={!newPost.trim() && newPostImages.length === 0}
              className="bg-primary hover:bg-primary/90"
              data-testid="submit-post-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>

        {/* Posts Feed */}
        <div className="space-y-6">
          {posts.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <Users className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="font-serif text-xl text-secondary-900 mb-2">No posts yet</h3>
              <p className="text-secondary-600">Be the first to share with the community!</p>
            </div>
          ) : (
            posts.map((post) => (
              <div
                key={post.id}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden ${
                  post.is_system_post ? 'border-2 border-primary/30' : ''
                }`}
                data-testid={`post-${post.id}`}
              >
                {/* Post Header */}
                <div className="p-4 border-b border-stone-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                        post.is_system_post 
                          ? 'bg-gradient-to-br from-primary to-accent' 
                          : 'bg-gradient-to-br from-pink-400 to-purple-500'
                      }`}>
                        {post.is_system_post ? '✿' : post.display_name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-secondary-900">
                          {post.display_name}
                          {post.is_admin && !post.is_system_post && (
                            <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">
                              Admin
                            </span>
                          )}
                          {post.is_system_post && (
                            <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              Official
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-secondary-500">{formatDate(post.created_at)}</p>
                      </div>
                    </div>
                    {(post.user_id === currentUserId || post.user_id === 'radiant-system') && !post.is_system_post && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeletePost(post.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-4">
                  <p className="text-secondary-800 whitespace-pre-line mb-4">{post.content}</p>
                  
                  {/* Images */}
                  {post.images && post.images.length > 0 && (
                    <div className={`grid gap-2 mb-4 ${
                      post.images.length === 1 ? 'grid-cols-1' :
                      post.images.length === 2 ? 'grid-cols-2' :
                      'grid-cols-2 md:grid-cols-3'
                    }`}>
                      {post.images.map((url, index) => {
                        // Handle both Cloudinary URLs and legacy local URLs
                        let imageUrl = url;
                        if (url.startsWith('/api/')) {
                          // Legacy local upload - construct full URL
                          imageUrl = `${API.replace('/api', '')}${url}`;
                        }
                        // Cloudinary URLs (https://res.cloudinary.com/...) are used as-is
                        return (
                          <img
                            key={index}
                            src={imageUrl}
                            alt={`Post image ${index + 1}`}
                            className="w-full max-h-96 object-contain rounded-lg bg-gray-100"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Hashtags */}
                  {post.hashtags && post.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.hashtags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-sm text-primary bg-primary/10 px-2 py-1 rounded-full flex items-center gap-1"
                        >
                          <Hash className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Post Actions */}
                <div className="px-4 py-3 border-t border-stone-100 flex items-center gap-6">
                  <button
                    onClick={() => handleLike(post.id)}
                    className={`flex items-center gap-2 transition-colors ${
                      post.likes?.includes(currentUserId)
                        ? 'text-red-500'
                        : 'text-secondary-500 hover:text-red-500'
                    }`}
                    data-testid={`like-btn-${post.id}`}
                  >
                    <Heart
                      className={`w-5 h-5 ${post.likes?.includes(currentUserId) ? 'fill-current' : ''}`}
                    />
                    <span className="text-sm">{post.likes?.length || 0}</span>
                  </button>
                  <button
                    onClick={() => setExpandedComments(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                    className="flex items-center gap-2 text-secondary-500 hover:text-primary transition-colors"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="text-sm">{post.comments?.length || 0}</span>
                  </button>
                </div>

                {/* Comments Section */}
                {expandedComments[post.id] && (
                  <div className="px-4 pb-4 border-t border-stone-100 pt-4">
                    {/* Existing Comments */}
                    {post.comments && post.comments.length > 0 && (
                      <div className="space-y-3 mb-4">
                        {post.comments.map((comment) => (
                          <div key={comment.id} className="flex gap-3 bg-stone-50 rounded-lg p-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-teal-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                              {comment.display_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="font-medium text-sm text-secondary-900">
                                  {comment.display_name}
                                  {comment.is_admin && (
                                    <span className="ml-1.5 text-[9px] bg-purple-100 text-purple-700 px-1 py-0.5 rounded-full font-medium">
                                      Admin
                                    </span>
                                  )}
                                </p>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-secondary-500">{formatDate(comment.created_at)}</span>
                                  {comment.user_id === currentUserId && (
                                    <button
                                      onClick={() => handleDeleteComment(post.id, comment.id)}
                                      className="text-red-400 hover:text-red-500"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-secondary-700 mt-1">{comment.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Comment */}
                    <div className="flex gap-2">
                      <Input
                        value={commentText[post.id] || ''}
                        onChange={(e) => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                        placeholder="Write a comment..."
                        className="flex-1"
                        onKeyPress={(e) => e.key === 'Enter' && handleComment(post.id)}
                      />
                      <Button
                        onClick={() => handleComment(post.id)}
                        disabled={!commentText[post.id]?.trim()}
                        size="icon"
                        className="bg-primary hover:bg-primary/90"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}

          {/* Load More */}
          {hasMore && posts.length > 0 && (
            <div className="text-center">
              <Button
                variant="outline"
                onClick={loadMore}
                className="px-8"
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default CommunityPage;
